const CSS = `
#party-hud {
  position: fixed;
  inset: 0;
  z-index: 11;
  pointer-events: none;
  overflow: hidden;
  --safe-top: env(safe-area-inset-top, 0px);
  --safe-bottom: env(safe-area-inset-bottom, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
  --safe-right: env(safe-area-inset-right, 0px);
}

#party-roster {
  position: absolute;
  top: calc(max(10px, env(safe-area-inset-top)) + 34px);
  right: max(10px, env(safe-area-inset-right));
  display: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding: 5px 10px 6px;
  border-radius: 2px;
  background: rgba(9, 8, 7, 0.86);
  color: #e9e2d0;
  font-family: "Silkscreen", monospace;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}
#party-roster[data-visible="true"] {
  display: flex;
}
#party-hud[data-night="true"] #party-roster {
  background: rgba(233, 226, 208, 0.93);
  color: #14120f;
}

/* the off-screen indicator is the same tag as the in-world nameplate */
.party-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 2px 7px 3px;
  background: rgba(9, 8, 7, 0.82);
  border-radius: 2px;
  color: #e9e2d0;
  font-family: "Silkscreen", monospace;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  opacity: 0.92;
}
`;


const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

export interface Marker {
  element: HTMLDivElement;
  name: string;
}

export interface PartyTarget {
  id: string;
  name: string;
  x: number;
  y: number;
  visible: boolean;
}

export interface ScreenBox {
  left: number;
  top: number;
  width: number;
  height: number;
  safeTop: number;
  safeBottom: number;
  safeLeft: number;
  safeRight: number;
}

export class PartyHud {
  private readonly root: HTMLDivElement;
  private readonly roster: HTMLDivElement;
  private readonly markers = new Map<string, Marker>();

  constructor() {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    this.root = document.createElement("div");
    this.root.id = "party-hud";

    this.roster = document.createElement("div");
    this.roster.id = "party-roster";
    this.roster.dataset.visible = "false";

    this.root.appendChild(this.roster);
    document.body.appendChild(this.root);
  }

  setNight(on: boolean): void {
    this.root.dataset.night = on ? "true" : "false";
  }

  setRoster(names: string[]): void {
    const signature = names.join("|");
    if (this.roster.dataset.signature !== signature) {
      this.roster.dataset.signature = signature;
      this.roster.replaceChildren(
        ...names.map((name) => {
          const span = document.createElement("span");
          span.textContent = name;
          return span;
        }),
      );
    }
    this.roster.dataset.visible = names.length > 0 ? "true" : "false";
  }

  safeInsets(): Pick<ScreenBox, "safeTop" | "safeBottom" | "safeLeft" | "safeRight"> {
    const style = getComputedStyle(this.root);
    const read = (name: string): number => Number.parseFloat(style.getPropertyValue(name)) || 0;
    return {
      safeTop: read("--safe-top"),
      safeBottom: read("--safe-bottom"),
      safeLeft: read("--safe-left"),
      safeRight: read("--safe-right"),
    };
  }

  setMarkers(targets: PartyTarget[], box: ScreenBox): void {
    // inset relative to the smaller viewport axis so the tag sits the same
    // distance in on a phone and on an ultrawide
    const pad = Math.max(26, Math.min(box.width, box.height) * 0.06);
    const minX = box.left + box.safeLeft + pad;
    const maxX = box.left + box.width - box.safeRight - pad;
    const minY = box.top + box.safeTop + pad;
    const maxY = box.top + box.height - box.safeBottom - pad;
    const originX = box.left + box.width / 2;
    const originY = box.top + box.height / 2;
    const halfWidth = box.width / 2;
    const halfHeight = box.height / 2;
    const seen = new Set<string>();

    for (const target of targets) {
      seen.add(target.id);
      let marker = this.markers.get(target.id);
      if (!marker) {
        const element = document.createElement("div");
        element.className = "party-marker";
        element.textContent = target.name;
        this.root.appendChild(element);
        marker = { element, name: target.name };
        this.markers.set(target.id, marker);
      }
      if (marker.name !== target.name) {
        marker.name = target.name;
        marker.element.textContent = target.name;
      }

      if (target.visible) {
        marker.element.style.display = "none";
        continue;
      }

      marker.element.style.display = "";
      const halfMarkerW = (marker.element.offsetWidth || 44) / 2;
      const halfMarkerH = (marker.element.offsetHeight || 14) / 2;
      const lowX = Math.min(minX + halfMarkerW, maxX - halfMarkerW);
      const highX = Math.max(minX + halfMarkerW, maxX - halfMarkerW);
      const lowY = Math.min(minY + halfMarkerH, maxY - halfMarkerH);
      const highY = Math.max(minY + halfMarkerH, maxY - halfMarkerH);

      // project, then pin each axis to the frame — the tag stays directly under the
      // player and slides along the edge, so it lines up when they walk into frame
      marker.element.style.left = `${clamp(originX + target.x * halfWidth, lowX, highX).toFixed(1)}px`;
      marker.element.style.top = `${clamp(originY - target.y * halfHeight, lowY, highY).toFixed(1)}px`;
    }

    for (const [id, marker] of this.markers) {
      if (seen.has(id)) continue;
      marker.element.remove();
      this.markers.delete(id);
    }
  }
}
